import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'

import {
  FETCH_CRUISE_DEALS,
  FETCH_CRUISE_DEALS_BY_SLUG,
  FETCH_CRUISE_LINE,
  FETCH_CRUISE_PROMO_BANNERS, FETCH_CRUISES_LIST_RECOMMENDATION,
  FETCH_CRUISE_OFFER,
  FETCH_CRUISE_SEARCH_FACETS,
  FETCH_CRUISE_SEARCH_COUNT,
  FETCH_CRUISE_BOOKING_CABIN_RELEASE,
  FETCH_CRUISE_BOOKING_RATE_LIST,
  FETCH_CRUISE_BOOKING_CABIN_LIST,
  FETCH_CRUISE_BOOKING_CABIN_DETAILS_LIST,
  FETCH_CRUISE_BOOKING_CABIN_SELECTION,
  FETCH_CRUISE_BOOKING_CABIN_PRICING,
  FETCH_CRUISE_BOOKING_RATE_DETAILS_LIST,
  FETCH_CRUISE_BOOKING_BY_ID_V1,
  FETCH_CRUISE_DEPARTURE_BY_ID,
  RESET_SESSION_ID,
  FETCH_CRUISE_SHIP,
  FETCH_CRUISE_CONSOLIDATED_PAYMENT_SCHEDULE,
  FETCH_CRUISE_OFFER_SNAPSHOT,
  FETCH_CRUISE_BOOKING_BY_ID,
} from 'actions/apiActionConstants'

import {
  API_CALL_REQUEST,
  API_CALL_FAILURE,
  API_CALL_SUCCESS,
  SAVE_CABIN_OPTION,
  RESET_REVELEX_CALLS,
  RESET_CABIN_AVAILABILITY,
  CRUISE_SET_SEARCH_FACETS,
  CRUISE_SET_CRUISE_LINES,
  RESET_BOOKING_CABIN_SELECTION,
  SET_BOOKING_INTEREST_IN_CONNECTED_CABINS,
  SET_CRUISE_OFFER_PLACES,
} from 'actions/actionConstants'
import { AnyAction } from 'redux'
import { omitKeys } from 'lib/object/objectUtils'

export const initialCruiseState: App.CruiseState = {
  endOfPagination: false,
  fetchingCruises: true,
  cruiseIdListRecommendation: [],

  // PROMO BANNERS
  cruisePromoBanners: [],
  fetchingCruisePromoBanners: false,

  // SEARCH FACETS/FILTERS
  cruiseFacets: {},
  cruiseSearchCount: {},

  // OFFERS
  cruiseOffers: {},
  cruiseOffersLoading: {},
  cruiseOffersErrors: {},

  // OFFER PLACES - departure and return places by offer ID
  offerPlaces: {},

  // OFFER SHIP DATA
  ships: {},

  // CRUISE LINES
  cruiseLines: {},
  fetchingCruiseLines: false,

  // DEPARTURES
  cruiseDeparture: {},
  cruiseDepartureError: {},
  cruiseDepartureLoading: {},

  // CRUISE DEALS
  cruiseDeals: [],
  fetchingCruiseDeals: false,
  cruiseDealsBySlug: {},

  // BOOKING INFO
  cruiseBookingInfoV1: {},
  cruiseBookingInfoV1Error: {},
  cruiseBookingInfoV1Loading: {},

  // LEGACY BOOKING FLOW - TODO: move to multiBooking object
  sessionIDExpired: false,

  // CRUISE OFFER SNAPSHOT
  cruiseOfferSnapshot: {},
  cruiseOfferSnapshotError: {},
  cruiseOfferSnapshotLoading: {},

  // CRUISE BOOKING V2
  cruiseBooking: {},
  cruiseBookingError: {},
  cruiseBookingLoading: {},

  // BOOKING FLOW
  multiBooking: {
    interestInConnectedCabins: false,
    consolidatedPaymentSchedule: {
      error: false,
      loading: false,
      schedule: undefined,
    },
    /*
     * Data stored by cabin passenger hash ex: "adults_2_children_3"
     * this means that cabins with the same passenger configuration can share the same data
     * without needing to request the API for each cabin
    */
    rateList: {},
    cabinList: {},
    cabinDetails: {},
    rateDetails: {},

    /*
     * Data stored by cart item ID
     * Even if all selected cabins have the same passenger configuration,
     * each cabin will have individual data as session, bookingId, and pricing
    */
    session: {},
    cabinSelection: {},
    cabinPricing: {},
    cabinRelease: {},
  },
}

const apiCalls = apiReducerSwitch<App.CruiseState>({
  [FETCH_CRUISE_LINE]: {
    request: () => ({
      fetchingCruiseLines: true,
    }),
    success: (state, action) => ({
      cruiseLines: {
        ...state.cruiseLines,
        [action.data.id]: action.data,
      },
      fetchingCruiseLines: false,
    }),
  },
  [FETCH_CRUISE_DEALS]: {
    request: () => ({
      fetchingCruiseDeals: true,
    }),
    success: (_, action) => ({
      cruiseDeals: action.data,
      fetchingCruiseDeals: false,
    }),
    failure: () => ({
      fetchingCruiseDeals: false,
      cruiseDeals: [],
    }),
  },
  [FETCH_CRUISE_DEALS_BY_SLUG]: {
    request: (state, action) => ({
      cruiseDealsBySlug: {
        ...state.cruiseDealsBySlug,
        [action.slug]: {
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      cruiseDealsBySlug: {
        ...state.cruiseDealsBySlug,
        [action.slug]: {
          deal: action.data,
        },
      },
    }),
    failure: (state, action) => ({
      cruiseDealsBySlug: {
        ...state.cruiseDealsBySlug,
        [action.slug]: {
          error: action.error?.message,
        },
      },
    }),
  },
  [FETCH_CRUISE_SEARCH_FACETS]: {
    request: (state, action) => ({
      cruiseFacets: {
        ...state.cruiseFacets,
        [action.key]: {
          fetching: true,
          facets: [],
        },
      },
    }),
    success: (state, action) => ({
      cruiseFacets: {
        ...state.cruiseFacets,
        [action.key]: {
          fetching: false,
          facets: action.data,
        },
      },
    }),
    failure: (state, action) => ({
      cruiseFacets: {
        ...state.cruiseFacets,
        [action.key]: {
          fetching: false,
          facets: [],
          error: action.error,
        },
      },
    }),
  },
  [FETCH_CRUISE_SEARCH_COUNT]: {
    request: (state, action) => ({
      cruiseSearchCount: {
        ...state.cruiseSearchCount,
        [action.key]: {
          count: 0,
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      cruiseSearchCount: {
        ...state.cruiseSearchCount,
        [action.key]: {
          count: action.data,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      cruiseSearchCount: {
        ...state.cruiseSearchCount,
        [action.key]: {
          count: 0,
          fetching: false,
        },
      },
    }),
  },
  [FETCH_CRUISE_PROMO_BANNERS]: {
    request: () => ({
      fetchingCruisePromoBanners: true,
    }),
    success: (state, action) => ({
      cruisePromoBanners: action.data,
      fetchingCruisePromoBanners: false,
    }),
    failure: () => ({
      fetchingCruisePromoBanners: false,
    }),
  },
  [FETCH_CRUISE_OFFER]: {
    request: (state, action) => ({
      ...state,
      cruiseOffersLoading: {
        ...state.cruiseOffersLoading,
        [action.offerId]: true,
      },
    }),
    success: (state, action) => ({
      ...state,
      cruiseOffersLoading: {
        ...state.cruiseOffersLoading,
        [action.offerId]: false,
      },
      cruiseOffers: {
        ...state.cruiseOffers,
        [action.offerId]: action.data,
      },
    }),
    failure: (state, action) => ({
      ...state,
      cruiseOffersLoading: {
        ...state.cruiseOffersLoading,
        [action.offerId]: false,
      },
      cruiseOffersErrors: {
        ...state.cruiseOffersErrors,
        [action.offerId]: action.error,
      },
    }),
  },
  [FETCH_CRUISE_BOOKING_BY_ID_V1]: {
    request: (state, action) => ({
      ...state,
      cruiseBookingInfoV1Error: {
        ...state.cruiseBookingInfoV1Error,
        [action.id]: false,
      },
      cruiseBookingInfoV1Loading: {
        ...state.cruiseBookingInfoV1Loading,
        [action.id]: true,
      },
    }),
    success: (state, action) => ({
      ...state,
      cruiseBookingInfoV1Error: {
        ...state.cruiseBookingInfoV1Error,
        [action.id]: false,
      },
      cruiseBookingInfoV1Loading: {
        ...state.cruiseBookingInfoV1Loading,
        [action.id]: false,
      },
      cruiseBookingInfoV1: {
        ...state.cruiseBookingInfoV1,
        [action.id]: action.data,
      },
    }),
    failure: (state, action) => ({
      ...state,
      cruiseBookingInfoV1Error: {
        ...state.cruiseBookingInfoV1Error,
        [action.id]: true,
      },
      cruiseBookingInfoV1Loading: {
        ...state.cruiseBookingInfoV1Loading,
        [action.id]: false,
      },
    }),
  },
  [FETCH_CRUISE_DEPARTURE_BY_ID]: {
    request: (state, action) => ({
      ...state,
      cruiseDepartureError: {
        ...state.cruiseDepartureError,
        [action.departureId]: false,
      },
      cruiseDepartureLoading: {
        ...state.cruiseDepartureLoading,
        [action.departureId]: true,
      },
    }),
    success: (state, action) => ({
      ...state,
      cruiseDepartureError: {
        ...state.cruiseDepartureError,
        [action.departureId]: false,
      },
      cruiseDepartureLoading: {
        ...state.cruiseDepartureLoading,
        [action.departureId]: false,
      },
      cruiseDeparture: {
        ...state.cruiseDeparture,
        [action.departureId]: action.data,
      },
    }),
    failure: (state, action) => ({
      ...state,
      cruiseDepartureError: {
        ...state.cruiseDepartureError,
        [action.departureId]: true,
      },
      cruiseDepartureLoading: {
        ...state.cruiseDepartureLoading,
        [action.departureId]: false,
      },
    }),
  },
  [FETCH_CRUISE_BOOKING_RATE_LIST]: {
    request: (state, action) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        rateList: {
          ...state.multiBooking.rateList,
          [action.cartItemId]: {
            rates: [],
            loading: true,
            error: false,
          },
        },
      },
    }),
    success: (state, action) => {
      return {
        ...state,
        multiBooking: {
          ...state.multiBooking,
          rateList: {
            ...state.multiBooking.rateList,
            [action.cartItemId]: {
              rates: action.data.cabinRatePackages,
              loading: false,
              error: false,
            },
          },
          session: {
            ...state.multiBooking.session,
            [action.cartItemId]: {
              sessionId: action.data.sessionId,
              bookingId: action.data.bookingId,
              cabinHoldTimeout: action.data.cabinHoldTimeout,
              personTitles: action.data.personTitles,
              sharedSessionId: undefined,
            },
          },
        },
      }
    },
    failure: (state, action) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        rateList: {
          ...state.multiBooking.rateList,
          [action.cartItemId]: {
            rates: [],
            loading: false,
            error: true,
            errors: action.error?.errors,
            errorMessage: action.error?.message,
            errorToRender: action.error?.errorToRender,
          },
        },
        session: omitKeys([action.cartItemId], state.multiBooking.session),
      },
    }),
  },
  [FETCH_CRUISE_BOOKING_CABIN_LIST]: {
    request: (state, action) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        cabinList: {
          ...state.multiBooking.cabinList,
          [action.travelerKey]: {
            cabins: [],
            cabinByNumber: {},
            loading: true,
            erroredCabinComponentIds: [],
            error: false,
          },
        },
      },
    }),
    success: (state, action) => {
      return {
        ...state,
        multiBooking: {
          ...state.multiBooking,
          cabinList: {
            ...state.multiBooking.cabinList,
            [action.travelerKey]: {
              cabins: action.data.cabins,
              cabinByNumber: action.data.cabinByNumber,
              loading: false,
              erroredCabinComponentIds: action.data.erroredCabinComponentIds,
              error: false,
            },
          },
        },
      }
    },
    failure: (state, action) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        cabinList: {
          ...state.multiBooking.cabinList,
          [action.travelerKey]: {
            cabins: [],
            cabinByNumber: {},
            loading: false,
            erroredCabinComponentIds: [],
            error: true,
            errors: action.error?.errors,
            errorMessage: action.error?.message,
            errorToRender: action.error?.errorToRender,
          },
        },
      },
    }),
  },
  [FETCH_CRUISE_BOOKING_CABIN_DETAILS_LIST]: {
    request: (state, action) => {
      const detailsData = state.multiBooking.cabinDetails[action.travelerKey] || {
        detailsByCabin: {},
        errorByCabin: {},
      }

      return {
        ...state,
        multiBooking: {
          ...state.multiBooking,
          cabinDetails: {
            ...state.multiBooking.cabinDetails,
            [action.travelerKey]: {
              ...detailsData,
              loading: true,
            },
          },
        },
      }
    },
    success: (state, action) => {
      const detailsData = state.multiBooking.cabinDetails[action.travelerKey]
      return {
        ...state,
        multiBooking: {
          ...state.multiBooking,
          cabinDetails: {
            ...state.multiBooking.cabinDetails,
            [action.travelerKey]: {
              ...detailsData,
              detailsByCabin: {
                ...detailsData.detailsByCabin,
                ...action.data,
              },
              loading: false,
            },
          },
        },
      }
    },
    failure: (state, action: AnyAction & { travelerKey: string, cabinNumbers: Array<string> }) => {
      const { cabinNumbers, travelerKey } = action
      const detailsData = state.multiBooking.cabinDetails[travelerKey]

      // set error for each cabinNumber
      const errorByCabin = {
        ...detailsData.errorByCabin,
        ...cabinNumbers.reduce((acc, cabinNumber) => ({ ...acc, [cabinNumber]: true }), {}),
      }

      return {
        ...state,
        multiBooking: {
          ...state.multiBooking,
          cabinDetails: {
            ...state.multiBooking.cabinDetails,
            [travelerKey]: {
              ...detailsData,
              loading: false,
              errorByCabin,
            },
          },
        },
      }
    },
  },
  [FETCH_CRUISE_BOOKING_CABIN_SELECTION]: {
    request: (state, action) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        cabinSelection: {
          ...state.multiBooking.cabinSelection,
          [action.cruiseItemId]: {
            cabinSelection: undefined,
            loading: true,
            error: false,
          },
        },
        consolidatedPaymentSchedule: {
          error: false,
          loading: false,
          schedule: undefined,
        },
      },
    }),
    success: (state, action) => {
      return {
        ...state,
        multiBooking: {
          ...state.multiBooking,
          cabinSelection: {
            ...state.multiBooking.cabinSelection,
            [action.cruiseItemId]: {
              cabinSelection: action.data,
              loading: false,
              error: false,
            },
          },
          // Reset cabinPricing when cabin selection is updated
          cabinPricing: {
            ...state.multiBooking.cabinPricing,
            [action.cruiseItemId]: {
              cabinPricing: undefined,
              loading: false,
              error: false,
              errorToRender: undefined,
              dirtyPreferences: false,
            },
          },
        },
      }
    },
    failure: (state, action) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        cabinSelection: {
          ...state.multiBooking.cabinSelection,
          [action.cruiseItemId]: {
            cabinSelection: undefined,
            loading: false,
            error: true,
            errors: action.error?.errors,
            errorMessage: action.error?.message,
            errorToRender: action.error?.errorToRender,
          },
        },
        cabinRelease: {
          ...state.multiBooking.cabinRelease,
          [action.cruiseItemId]: {
            released: false,
            loading: false,
            error: false,
          },
        },
      },
    }),
  },
  [FETCH_CRUISE_BOOKING_CABIN_PRICING]: {
    request: (state, action) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        cabinPricing: {
          ...state.multiBooking.cabinPricing,
          [action.cruiseItemId]: {
            cabinPricing: undefined,
            loading: true,
            error: false,
            errorToRender: undefined,
          },
        },
        consolidatedPaymentSchedule: {
          error: false,
          loading: false,
          schedule: undefined,
        },
      },
    }),
    success: (state, action) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        cabinPricing: {
          ...state.multiBooking.cabinPricing,
          [action.cruiseItemId]: {
            cabinPricing: action.data,
            loading: false,
            error: false,
            errorToRender: undefined,
            dirtyPreferences: false,
          },
        },
      },
    }),
    failure: (state, action) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        cabinPricing: {
          ...state.multiBooking.cabinPricing,
          [action.cruiseItemId]: {
            cabinPricing: undefined,
            loading: false,
            error: true,
            errors: action.error?.errors,
            errorMessage: action.error?.message,
            errorToRender: action.error?.errorToRender,
            dirtyPreferences: false,
          },
        },
      },
    }),
  },
  [FETCH_CRUISE_BOOKING_RATE_DETAILS_LIST]: {
    request: (state, action) => {
      const detailsData = state.multiBooking.rateDetails[action.cartItemId] || {
        detailsByRate: {},
        errorByRate: {},
        loadingByRate: {},
      }

      return {
        ...state,
        multiBooking: {
          ...state.multiBooking,
          rateDetails: {
            ...state.multiBooking.rateDetails,
            [action.cartItemId]: {
              ...detailsData,
              loadingByRate: {
                ...detailsData.loadingByRate,
                [action.componentId]: true,
              },
            },
          },
        },
      }
    },
    success: (state, action) => {
      const detailsData = state.multiBooking.rateDetails[action.cartItemId]

      return {
        ...state,
        multiBooking: {
          ...state.multiBooking,
          rateDetails: {
            ...state.multiBooking.rateDetails,
            [action.cartItemId]: {
              ...detailsData,
              loadingByRate: {
                ...detailsData.loadingByRate,
                [action.componentId]: false,
              },
              detailsByRate: {
                ...detailsData.detailsByRate,
                ...action.data,
              },
            },
          },
        },
      }
    },
    failure: (state, action) => {
      const detailsData = state.multiBooking.rateDetails[action.cartItemId]

      return {
        ...state,
        multiBooking: {
          ...state.multiBooking,
          rateDetails: {
            ...state.multiBooking.rateDetails,
            [action.cartItemId]: {
              ...detailsData,
              loadingByRate: {
                ...detailsData.loadingByRate,
                [action.componentId]: false,
              },
              errorByRate: {
                ...detailsData.errorByRate,
                [action.componentId]: true,
              },
            },
          },
        },
      }
    },
  },
  [FETCH_CRUISE_BOOKING_CABIN_RELEASE]: {
    request: (state, action) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        cabinSelection: omitKeys([action.cruiseItemId], state.multiBooking.cabinSelection),
        cabinPricing: omitKeys([action.cruiseItemId], state.multiBooking.cabinPricing),
        cabinRelease: {
          ...state.multiBooking.cabinRelease,
          [action.cruiseItemId]: {
            released: false,
            loading: true,
            error: false,
          },
        },
        consolidatedPaymentSchedule: {
          error: false,
          loading: false,
          schedule: undefined,
        },
      },
    }),
    success: (state, action) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        cabinRelease: {
          ...state.multiBooking.cabinRelease,
          [action.cruiseItemId]: {
            released: true,
            loading: false,
            error: false,
          },
        },
      },
    }),
    failure: (state, action) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        cabinRelease: {
          ...state.multiBooking.cabinRelease,
          [action.cruiseItemId]: {
            released: false,
            loading: false,
            error: true,
          },
        },
      },
    }),
  },
  [FETCH_CRUISE_CONSOLIDATED_PAYMENT_SCHEDULE]: {
    request: (state) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        consolidatedPaymentSchedule: {
          error: false,
          loading: true,
          schedule: undefined,
        },
      },
    }),
    success: (state, action) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        consolidatedPaymentSchedule: {
          error: false,
          loading: false,
          schedule: action.data,
        },
      },
    }),
    failure: (state) => ({
      ...state,
      multiBooking: {
        ...state.multiBooking,
        consolidatedPaymentSchedule: {
          error: true,
          loading: false,
          schedule: undefined,
        },
      },
    }),
  },
  [FETCH_CRUISE_OFFER_SNAPSHOT]: {
    request: (state, action) => ({
      ...state,
      cruiseOfferSnapshotError: {
        ...state.cruiseOfferSnapshotError,
        [action.bookingId]: false,
      },
      cruiseOfferSnapshotLoading: {
        ...state.cruiseOfferSnapshotLoading,
        [action.bookingId]: true,
      },
    }),
    success: (state, action) => ({
      ...state,
      cruiseOfferSnapshotError: {
        ...state.cruiseOfferSnapshotError,
        [action.bookingId]: false,
      },
      cruiseOfferSnapshotLoading: {
        ...state.cruiseOfferSnapshotLoading,
        [action.bookingId]: false,
      },
      cruiseOfferSnapshot: {
        ...state.cruiseOfferSnapshot,
        [action.bookingId]: action.data,
      },
    }),
    failure: (state, action) => ({
      ...state,
      cruiseOfferSnapshotError: {
        ...state.cruiseOfferSnapshotError,
        [action.bookingId]: true,
      },
      cruiseOfferSnapshotLoading: {
        ...state.cruiseOfferSnapshotLoading,
        [action.bookingId]: false,
      },
    }),
  },
  [FETCH_CRUISE_BOOKING_BY_ID]: {
    request: (state, action) => ({
      ...state,
      cruiseBookingError: {
        ...state.cruiseBookingError,
        [action.bookingId]: false,
      },
      cruiseBookingLoading: {
        ...state.cruiseBookingLoading,
        [action.bookingId]: true,
      },
    }),
    success: (state, action) => ({
      ...state,
      cruiseBookingError: {
        ...state.cruiseBookingError,
        [action.bookingId]: false,
      },
      cruiseBookingLoading: {
        ...state.cruiseBookingLoading,
        [action.bookingId]: false,
      },
      cruiseBooking: {
        ...state.cruiseBooking,
        [action.bookingId]: action.data,
      },
    }),
    failure: (state, action) => ({
      ...state,
      cruiseBookingError: {
        ...state.cruiseBookingError,
        [action.bookingId]: true,
      },
      cruiseBookingLoading: {
        ...state.cruiseBookingLoading,
        [action.bookingId]: false,
      },
    }),
  },
  [FETCH_CRUISES_LIST_RECOMMENDATION]: {
    success: (state, action) => ({
      cruiseIdListRecommendation: action.data,
    }),
  },
  [FETCH_CRUISE_SHIP]: {
    success: (state, action) => ({
      ...state,
      ships: {
        ...state.ships,
        [action.shipId]: action.data,
      },
    }),
  },
})

const bookingRequests = new Set([
  FETCH_CRUISE_BOOKING_RATE_LIST,
  FETCH_CRUISE_BOOKING_CABIN_LIST,
  FETCH_CRUISE_BOOKING_CABIN_SELECTION,
  FETCH_CRUISE_BOOKING_CABIN_PRICING,
  FETCH_CRUISE_BOOKING_RATE_DETAILS_LIST,
])

export default createAppReducer(initialCruiseState, {
  [API_CALL_REQUEST]: apiCalls,
  [API_CALL_FAILURE]: (state, action) => {
    const isBookingRequest = bookingRequests.has(action.api)

    // Reset state if session has expired, so that the cabin rate request can be restarted
    if (isBookingRequest && action.error.message.includes('session has expired')) {
      return apiCalls({
        ...state,
        sessionIDExpired: true,
      }, action)
    }

    return apiCalls(state, action)
  },
  [API_CALL_SUCCESS]: apiCalls,
  [SAVE_CABIN_OPTION]: (state, action) => {
    return {
      ...state,
      multiBooking: {
        ...state.multiBooking,
        cabinPricing: {
          ...state.multiBooking.cabinPricing,
          [action.cruiseItemId]: {
            cabinPricing: undefined,
            loading: false,
            error: false,
            errorToRender: undefined,
            dirtyPreferences: true,
          },
        },
      },
    }
  },
  [RESET_SESSION_ID]: (state) => ({ ...state, revelexSessionId: null }),
  [RESET_REVELEX_CALLS]: (state) => {
    return {
      ...state,
      sessionIDExpired: false,
      // BOOKING V2
      multiBooking: {
        ...state.multiBooking,

        rateList: {},
        cabinList: {},
        cabinDetails: {},
        rateDetails: {},

        session: {},
        cabinRelease: {},
        cabinSelection: {},
        cabinPricing: {},
      },
    }
  },
  [RESET_CABIN_AVAILABILITY]: (state) => ({
    ...state,
    cabinAvailability: null,
    cabinAvailabilityError: false,
    cabinAvailabilityLoading: false,
  }),
  [RESET_BOOKING_CABIN_SELECTION]: (state) => ({
    ...state,
    multiBooking: {
      ...state.multiBooking,
      cabinSelection: {},
      cabinPricing: {},
    },
  }),
  [CRUISE_SET_SEARCH_FACETS]: (state, action) => ({
    cruiseFacets: {
      ...state.cruiseFacets,
      [action.key]: {
        fetching: false,
        facets: action.data,
      },
    },
  }),
  [CRUISE_SET_CRUISE_LINES]: (state, action) => ({
    cruiseLines: action.data,
  }),
  [SET_BOOKING_INTEREST_IN_CONNECTED_CABINS]: (state, action) => ({
    ...state,
    multiBooking: {
      ...state.multiBooking,
      interestInConnectedCabins: action.data,
    },
  }),
  [SET_CRUISE_OFFER_PLACES]: (state, action) => ({
    ...state,
    offerPlaces: {
      ...state.offerPlaces,
      [action.offerId]: {
        departurePlace: action.departurePlace,
        returnPlace: action.returnPlace,
      },
    },
  }),
})
